// src/gestion-logistics/components/ClientSelector.tsx
import React, { useState, useEffect } from "react";
import { Select, Spin } from "antd";
import { UserOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../../config/api";
import { deduplicateEntities } from "../../utils/entityUtils";

const { Option } = Select;

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  company_id?: number;
  user_type?: string;
}

interface ClientSelectorProps {
  value?: number;
  onChange?: (value: number, clientName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  companyId?: number | null;  // Filtrer par entreprise depuis RightSideNav
  warehouseId?: number | null; // Filtrer par magasin depuis RightSideNav
}

/**
 * Composant de sélection de client avec autocomplete
 * Charge les clients depuis /api/users/customers (table users avec user_type='customers')
 */
const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  placeholder = "Rechercher un client...",
  disabled = false,
  companyId,
  warehouseId,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchClients = async (search: string = "") => {
    setLoading(true);
    try {
      // Construire les paramètres de requête pour filtrage multi-tenant
      const params: any = {};
      if (companyId) params.companyId = companyId;
      if (warehouseId) params.warehouseId = warehouseId;

      const response = await axios.get(`${API_CONFIG.API_URL}/users/customers`, { params });

      if (Array.isArray(response.data)) {
        // Dédoublonner les clients (évite les doublons de jointures SQL)
        const uniqueClients = deduplicateEntities(response.data);

        // Filtrer par recherche côté client si nécessaire
        const filtered = search
          ? uniqueClients.filter((client: any) =>
              client.name?.toLowerCase().includes(search.toLowerCase()) ||
              client.email?.toLowerCase().includes(search.toLowerCase())
            )
          : uniqueClients;

        const clientData = filtered.map((item: any) => ({
          id: item.id,
          name: item.name,
          email: item.email,
          phone: item.phone,
          company_id: item.company_id,
          user_type: item.user_type,
        }));

        setClients(clientData);
      }
    } catch (error) {
      console.error("[LOGISTICS] Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  // Recharger les clients quand companyId ou warehouseId change
  useEffect(() => {
    if (companyId) {
      fetchClients();
    } else {
      setClients([]);
    }
  }, [companyId, warehouseId]);

  const handleChange = (selectedId: number) => {
    const selectedClient = clients.find((c) => c.id === selectedId);
    if (selectedClient && onChange) {
      onChange(selectedId, selectedClient.name);
    }
  };

  const filterOption = (input: string, option: any) => {
    const label = option?.children?.props?.children?.[0]?.props?.children || "";
    const email = option?.children?.props?.children?.[1]?.props?.children || "";
    const searchTerm = input.toLowerCase();
    return (
      label.toString().toLowerCase().includes(searchTerm) ||
      email.toString().toLowerCase().includes(searchTerm)
    );
  };

  return (
    <Select
      showSearch
      value={value}
      placeholder={placeholder}
      onChange={handleChange}
      filterOption={filterOption}
      loading={loading}
      disabled={disabled}
      notFoundContent={loading ? <Spin size="small" /> : "Aucun client trouvé"}
      style={{ width: "100%" }}
      suffixIcon={<UserOutlined />}
    >
      {clients.map((client) => (
        <Option key={client.id} value={client.id}>
          <div>
            <div style={{ fontWeight: "bold" }}>{client.name}</div>
            {client.email && (
              <div style={{ fontSize: "12px", color: "#999" }}>
                {client.email}
              </div>
            )}
          </div>
        </Option>
      ))}
    </Select>
  );
};

export default ClientSelector;
